'use client';
import React from 'react';
import Image from '@repo/ui/src/components/Image/New';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { useIntersectionObserver } from '@repo/ui/src/hooks/useIntersectionObserver';
import usePrevious from '@repo/ui/src/hooks/usePrevious';
import { useScrollDirection } from '@repo/ui/src/hooks/useScrollDirection';
import { ArticleModuleProps } from '@repo/ui/src/types/articleModules';
import { useIsMD } from '@repo/ui/src/utils/useMediaQuery';
import YoutubeVideo from '@repo/ui/src/components/YoutubeVideo';
import Quote from '@repo/ui/src/components/Quote';
import Markdown from '@repo/ui/src/components/Markdown';

export interface Props extends ArticleModuleProps {
  setVisibleSection?: (section: number) => void;
}

const ArticleModule = ({
  headerText,
  markdownText,
  youtubeVideo,
  pullQuote,
  index,
  image,
  additionalImages = [],
  setVisibleSection,
}: Props) => {
  const contentRef = React.useRef<HTMLAnchorElement>(null);
  const isMD = useIsMD();
  const entry = useIntersectionObserver(contentRef);
  const previouslyIntersecting = usePrevious(entry?.isIntersecting);
  const scrollDirection = useScrollDirection();

  React.useEffect(() => {
    if (!entry || previouslyIntersecting === undefined || !scrollDirection) {
      return;
    }
    if (
      (scrollDirection.current === 'up' && !previouslyIntersecting && entry.isIntersecting) ||
      (scrollDirection.current === 'down' && previouslyIntersecting && !entry.isIntersecting)
    ) {
      setVisibleSection?.(index);
    }
  }, [entry?.isIntersecting, previouslyIntersecting]);

  return (
    <section className="tw-my-8 tw-max-w-paragraph tw-m-auto">
      <a
        // disable intersection observer on mobile or if section has no header
        ref={setVisibleSection && headerText && isMD ? contentRef : undefined}
      />
      {headerText && (
        <h2 className="tra-text-web-heading-4 tw-mb-4 tw-p-0" tabIndex={0} id={`${index}header`}>
          {headerText}
        </h2>
      )}
      {markdownText && (
        <Markdown
          components={{
            h3: ({ ...props }) => <h3 className="tra-text-web-sub-1 tw-mb-2" {...props} />,
            h4: ({ children }: { children: React.ReactNode }) => (
              <h4 className="tra-text-web-sub-2 tw-mb-2">{children}</h4>
            ),
            h5: ({ children }: { children: React.ReactNode }) => (
              <h5 className="tra-text-web-sub-2 tw-mb-2">{children}</h5>
            ),
            h6: ({ children }: { children: React.ReactNode }) => (
              <h6 className="tra-text-web-sub-2 tw-mb-2">{children}</h6>
            ),
          }}
          bodySize="large"
        >
          {markdownText}
        </Markdown>
      )}
      {pullQuote && (
        <div className="tw-mb-4">
          <Quote quote={pullQuote} alignment="center" />
        </div>
      )}
      {[image, ...additionalImages].filter(Boolean).map((image, index) => (
        <div key={index} className="tw-my-10">
          <Image
            className="tw-w-full tw-object-fit tw-object-cover"
            src={image!.url ?? buildImageSrc(image)}
            alt={image!.accessibilityLabel ?? ''}
            height={500}
            width={768}
          />
        </div>
      ))}
      {youtubeVideo?.externalVideoId && (
        <YoutubeVideo
          youtubeId={youtubeVideo.externalVideoId}
          {...youtubeVideo}
          className="tw-aspect-video"
        />
      )}
    </section>
  );
};

export default ArticleModule;
